/**
 * ListaDistribucionRepo sobre Supabase. Destinatarios de correo compartidos
 * (el admin completa los emails una vez y valen para todos).
 */

import type { ListaDistribucionRepo } from "../../repos/ListaDistribucionRepo";
import type { ListaDistribucion } from "@app-types/entities";
import type { GerenciaCodigo } from "@constants/gerencias";
import { SupabaseBaseRepo } from "./SupabaseBaseRepo";

const DESTINATARIOS_CANONICOS = [
  "PL", "back-PL", "PL2", "back-PL2", "JEFE", "GTE", "CFE", "ABAS", "ABAS lider",
  "ADMIN_PMIS", "FAC", "OPER", "MTTO", "COMM", "ALMACEN", "comprador",
  "PLANNER", "ADOR", "contratista", "INT", "back-INT", "Contabilidad",
  "Tesoreria", "HSE", "HSE lider", "Ambiental", "QA", "Seguridad fisica",
  "LEAN", "Repositorio conocimiento", "solicitante", "proveedor", "responsable",
] as const;

function normalizar(s: string): string {
  return s.toLowerCase().replace(/[-_\s]+/g, "").normalize("NFD").replace(/[̀-ͯ]/g, "");
}

export class SupabaseListaDistribucionRepo
  extends SupabaseBaseRepo<ListaDistribucion>
  implements ListaDistribucionRepo
{
  constructor(tenantId: string) {
    super("listaDistribucion", tenantId);
  }

  async list(): Promise<ListaDistribucion[]> {
    return this.listAll();
  }

  async get(id: string): Promise<ListaDistribucion | null> {
    return this.getById(id);
  }

  async resolveEmails(destinatario: string, gerenciaId?: GerenciaCodigo): Promise<string[]> {
    const todos = await this.listAll();
    const norm = normalizar(destinatario);
    const candidatos = todos.filter((l) => normalizar(l.destinatario) === norm);
    if (gerenciaId) {
      const enGerencia = candidatos.find((l) => l.gerenciaId === gerenciaId);
      if (enGerencia) return enGerencia.emails;
    }
    const match = candidatos.find((l) => !gerenciaId || l.gerenciaId === undefined);
    return match?.emails ?? [];
  }

  async create(
    data: Omit<ListaDistribucion, "id" | "createdAt" | "updatedAt" | "version">,
  ): Promise<string> {
    const now = new Date().toISOString();
    const nuevo: ListaDistribucion = {
      ...data,
      id: crypto.randomUUID(),
      tenantId: this.tenantId,
      version: 1,
      createdAt: now,
      updatedAt: now,
    };
    await this.createRaw(nuevo);
    return nuevo.id;
  }

  async update(
    id: string,
    data: Partial<ListaDistribucion>,
    expectedVersion: number,
  ): Promise<void> {
    const current = await this.get(id);
    if (!current) throw new Error(`ListaDistribucion ${id} no encontrada`);
    const updated: ListaDistribucion = {
      ...current,
      ...data,
      // Auditoria de creacion INMUTABLE: ver BaseRepo.update.
      createdBy: current.createdBy,
      createdAt: current.createdAt,
      version: current.version + 1,
      updatedAt: new Date().toISOString(),
    };
    await this.updateRaw(updated, expectedVersion);
  }

  async delete(id: string): Promise<void> {
    await this.deleteById(id);
  }

  async ensureSeed(userEmail: string, gerenciaId?: GerenciaCodigo): Promise<number> {
    // Los "slots" de destinatario son estructurales (con emails vacíos para
    // que el admin los complete); se crean en demo y operación.
    const existentes = await this.listAll();
    const set = new Set(
      existentes
        .filter((l) => (gerenciaId ? l.gerenciaId === gerenciaId : true))
        .map((l) => normalizar(l.destinatario)),
    );
    const faltantes = DESTINATARIOS_CANONICOS.filter((d) => !set.has(normalizar(d)));
    if (faltantes.length === 0) return 0;
    const now = new Date().toISOString();
    const nuevos: ListaDistribucion[] = faltantes.map((destinatario) => ({
      id: crypto.randomUUID(),
      tenantId: this.tenantId,
      ...(gerenciaId ? { gerenciaId } : {}),
      destinatario,
      emails: [],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: userEmail,
      updatedBy: userEmail,
    }));
    await this.insertManyRaw(nuevos);
    return nuevos.length;
  }
}
